
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { Button } from "../../components/ui/button"
import { Input } from "../../components/ui/input"
import { Textarea } from "../../components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select"
import { Plus, Search, ArrowLeft, ArrowRight, Zap } from "lucide-react"
import { Label } from "../../components/ui/label"
import { toast } from "react-hot-toast"
import { Avatar, AvatarFallback } from "../../components/ui/avatar"
import {
  createJobDraft,
  updateJobRequirements,
  updateJobTerms,
  publishJob,
  updateJob,
  getJobDetailsById,
  editJob,
} from "../../services/jobsapi"
import { getJobCategoriesList } from "../../services/candidateapi"
import { getCompanyListForSelection } from "../../services/companiesapi"
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import { Switch } from "../../components/ui/switch"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog"
import { generateGeminiContent } from '../../services/usersapi'
import { useRbac } from "../../context"
import { getAllUsers } from "../../services/usersapi"
import { X } from "lucide-react"

// Category options will be fetched from API

/** Temporary: hide floating AI Assistant on job form; set true to show again */
const SHOW_JOB_FORM_AI_ASSISTANT = false

const shiftTypeOptions = [
  "Day Shifts", "Night Shifts", "Evening Shifts", "Morning Shifts",
  "Rotating Shifts", "Split Shifts", "Flexi-shifts", "Weekend Shifts",
  "On-call Shifts", "Other"
]

interface Company {
  id: string
  name: string
  logoUrl?: string
  isExternalCompany?: boolean
}

interface JobData {
  id: string
  title?: string
  companyId?: string
  company?: {
    id?: string
    name?: string
    logoUrl?: string
  }
  location?: string
  workType?: "on-site" | "remote" | "hybrid"
  addressDetails?: {
    street?: string
    area?: string
    city?: string
    pincode?: string
  }
  locationLink?: string
  skills?: {
    requiredSkills?: string[]
    preferredSkills?: string[]
    niceToHaveSkills?: string[]
  }
  minExperience?: number | string
  maxExperience?: number | string
  qualificationRequirements?: string[]
  languageRequirements?: string[]
  minAge?: number
  maxAge?: number
  employmentType?: string
  category?: string
  minSalary?: number
  maxSalary?: number
  currency?: string
  noOfOpenings?: number
  shiftTimings?: string
  tenureInDays?: number
  paymentFrequency?: "yearly" | "monthly" | "daily" | "weekly" | "hourly"
  commissionType?: "fixed" | "percentage"
  commissionAmount?: number
  commissionPercentage?: number
  hiringNeed?: "Immediate" | "Future"
  description?: string
  aiEnhancedDescription?: string
  suggestedKeywordsForSeo?: string[]
  visibility?: "public" | "internal" | "franchise_specific"
  status?: string
  isExternal?: boolean
  coordinators?: string[] // Array of coordinator user IDs (SHM role)
}






const skillSuggestions = [
  "JavaScript",
  "TypeScript",
  "Python",
  "Java",
  "C++",
  "C#",
  "PHP",
  "Ruby",
  "Go",
  "Rust",
  "Swift",
  "Kotlin",
  "Scala",
  "R",
  "MATLAB",
  "React",
  "Angular",
  "Vue.js",
  "HTML",
  "CSS",
  "SCSS",
  "Bootstrap",
  "Tailwind CSS",
  "jQuery",
  "Next.js",
  "Nuxt.js",
  "Svelte",
  "Node.js",
  "Express.js",
  "Django",
  "Flask",
  "Spring Boot",
  "Laravel",
  "Ruby on Rails",
  "ASP.NET",
  "FastAPI",
  "GraphQL",
  "MySQL",
  "PostgreSQL",
  "MongoDB",
  "Redis",
  "SQLite",
  "Oracle",
  "SQL Server",
  "Cassandra",
  "DynamoDB",
  "Firebase",
  "AWS",
  "Azure",
  "Google Cloud",
  "Docker",
  "Kubernetes",
  "Jenkins",
  "GitLab CI",
  "GitHub Actions",
  "Terraform",
  "Ansible",
  "React Native",
  "Flutter",
  "iOS Development",
  "Android Development",
  "Xamarin",
  "Ionic",
  "Git",
  "Jira",
  "Confluence",
  "Figma",
  "Adobe Creative Suite",
  "Sketch",
  "InVision",
  "Postman",
  "Selenium",
  "Jest",
]

const languageSuggestions = [
  "English",
  "Spanish",
  "French",
  "German",
  "Italian",
  "Portuguese",
  "Russian",
  "Chinese (Mandarin)",
  "Japanese",
  "Korean",
  "Arabic",
  "Hindi",
  "Bengali",
  "Urdu",
  "Tamil",
  "Telugu",
  "Marathi",
  "Gujarati",
  "Punjabi",
  "Malayalam",
  "Kannada",
  "Odia",
  "Assamese",
  "Dutch",
  "Swedish",
  "Norwegian",
  "Danish",
  "Finnish",
  "Polish",
  "Czech",
  "Hungarian",
  "Romanian",
  "Bulgarian",
  "Greek",
  "Turkish",
  "Hebrew",
  "Thai",
  "Vietnamese",
  "Indonesian",
  "Malay",
  "Tagalog",
  "Swahili",
]

const qualificationSuggestions = [
  { value: "10th", label: "Secondary Education (10th Grade)" },
  { value: "12th", label: "Higher Secondary Education (12th Grade)" },
  { value: "ITI", label: "Industrial Training Institute (ITI)" },
  { value: "Diploma", label: "Diploma" },
  { value: "Graduation (10 + 2 + 3)", label: "Bachelor's Degree (3 Years)" },
  { value: "Graduation (10 + 2 + 4)", label: "Bachelor's Degree (4 Years)" },
  { value: "Post Graduation", label: "Master's Degree" },
  { value: "PhD", label: "Doctorate (PhD)" },
];

const MultiSelectWithSuggestions = ({
  selectedItems,
  onAddItem,
  onRemoveItem,
  suggestions,
  placeholder,
  label,
  excludeItems = [],
}: {
  selectedItems: string[]
  onAddItem: (item: string) => void
  onRemoveItem: (item: string) => void
  suggestions: string[]
  placeholder: string
  label: string
  excludeItems?: string[]
}) => {
  const [inputValue, setInputValue] = useState("")
  const [open, setOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  
  const filteredSuggestions = suggestions
  .filter(
    (suggestion) =>
      suggestion.toLowerCase().includes(inputValue.toLowerCase()) && 
        !selectedItems.includes(suggestion) &&
        !excludeItems.includes(suggestion)
      )
      .slice(0, 8)
      
      const handleAddItem = (item: string) => {
        // For qualifications, only allow selection from suggestions
        if (label === "Qualifications") {
          if (suggestions.includes(item) && !selectedItems.includes(item) && (!excludeItems || !excludeItems.includes(item))) {
            onAddItem(item)
          }
          setInputValue("")
          setOpen(false)
          setHighlightedIndex(0)
          return
    }
    // ...existing code for other labels...
    if (label === "SEO Keywords" && item.includes(',')) {
      const items = item.split(',')
      .map(i => i.trim())
      .filter(i => i.length > 0)
      .filter(i => !selectedItems.includes(i) && (!excludeItems || !excludeItems.includes(i)))
      items.forEach(singleItem => {
        onAddItem(singleItem)
      })
      setInputValue("")
      setOpen(false)
      setHighlightedIndex(0)
      return
    }
    const trimmedItem = item.trim()
    if (trimmedItem.length > 0 && !selectedItems.includes(trimmedItem) && (!excludeItems || !excludeItems.includes(trimmedItem))) {
      onAddItem(trimmedItem)
    }
    setInputValue("")
    setOpen(false)
    setHighlightedIndex(0)
  }
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open && filteredSuggestions.length > 0 && inputValue) {
      setOpen(true)
    }
    
    switch (e.key) {
      case "Enter":
        e.preventDefault()
        if (open && filteredSuggestions.length > 0) {
          handleAddItem(filteredSuggestions[highlightedIndex])
        } else if (inputValue.trim()) {
          handleAddItem(inputValue.trim())
        }
        break
      case "ArrowDown":
        e.preventDefault()
        if (open && filteredSuggestions.length > 0) {
          setHighlightedIndex((prev) => (prev < filteredSuggestions.length - 1 ? prev + 1 : 0))
        }
        break
      case "ArrowUp":
        e.preventDefault()
        if (open && filteredSuggestions.length > 0) {
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : filteredSuggestions.length - 1))
        }
        break
      case "Escape":
        setOpen(false)
        setHighlightedIndex(0)
        break
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setInputValue(value)
    setOpen(value.length > 0)
    setHighlightedIndex(0)
  }

  return (
    <div className="space-y-3 max-w-[500px] w-full">
      <Label className="text-sm font-semibold text-gray-700">
        {label}
      </Label>
      <div className="relative">
        <div className="relative">
          <Input
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (inputValue && filteredSuggestions.length > 0) {
                setOpen(true)
              }
            }}
            onBlur={() => {   
              setTimeout(() => setOpen(false), 300)
            }}
            placeholder={placeholder}
            className="h-12 border-gray-200 focus:border-orange-500 focus:ring-orange-500/20 transition-all duration-200 max-w-[500px]"
          />
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
            {inputValue ? (
              <div className="flex items-center gap-1 text-xs text-gray-500">
                <span>Press Enter</span>
                <kbd className="px-1.5 py-0.5 bg-gray-100 border border-gray-300 rounded text-xs">↵</kbd>
              </div>
            ) : (
              <Search className="h-4 w-4 text-gray-400" />
            )}
          </div>
        </div>

        {open && inputValue && filteredSuggestions.length > 0 && (
          <div className="absolute z-50 w-full mt-2 bg-white border-2 border-orange-200 rounded-lg shadow-lg max-h-64 overflow-auto animate-in slide-in-from-top-2 duration-200">
            <div className="p-2">
              <div className="text-xs font-medium text-gray-500 px-3 py-2 border-b border-gray-100">
                {filteredSuggestions.length} suggestion{filteredSuggestions.length !== 1 ? "s" : ""} found
              </div>
              {filteredSuggestions.map((suggestion, index) => (
                <div
                  key={index}
                  className={`flex items-center justify-between px-3 py-3 mx-1 rounded-lg cursor-pointer transition-all duration-150 ${
                    index === highlightedIndex
                      ? "bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-md transform scale-[1.02]"
                      : "hover:bg-orange-50 text-gray-700 hover:text-orange-800"
                  }`}
                  onMouseDown={(e) => {
                    e.preventDefault()
                    handleAddItem(suggestion)
                  }}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-2 h-2 rounded-full ${index === highlightedIndex ? "bg-white" : "bg-orange-400"}`}
                    />
                    <span className="font-medium text-sm">
                      {suggestion.split(new RegExp(`(${inputValue})`, "gi")).map((part, i) => (
                        <span
                          key={i}
                          className={
                            part.toLowerCase() === inputValue.toLowerCase()
                              ? index === highlightedIndex
                                ? "bg-white/20 px-1 rounded"
                                : "bg-orange-100 text-orange-800 px-1 rounded font-semibold"
                              : ""
                          }
                        >
                          {part}
                        </span>
                      ))}
                    </span>
                  </div>
                  <div
                    className={`flex items-center gap-1 text-xs ${
                      index === highlightedIndex ? "text-white/80" : "text-orange-600"
                    }`}
                  >
                    {index === highlightedIndex && <kbd className="px-1.5 py-0.5 bg-white/20 rounded text-xs">↵</kbd>}
                    <Plus className="h-3 w-3" />
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-gray-100 p-3 bg-gray-50 rounded-b-lg">
              <div className="flex items-center justify-between text-xs text-gray-600">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1">
                    <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded">↑↓</kbd>
                    <span>Navigate</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded">↵</kbd>
                    <span>Select</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded">Esc</kbd>
                    <span>Close</span>
                  </div>
                </div>
                <span className="text-orange-600 font-medium">{selectedItems.length} selected</span>
              </div>
            </div>
          </div>
        )}

        {open && inputValue && filteredSuggestions.length === 0 && (
          <>
           
          </>
        )}
      </div>
        
      {selectedItems.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedItems.map((item, index) => (
            <span key={index} className="gap-2 py-2 px-4 bg-orange-50 text-orange-700 border border-orange-200 rounded-xl font-medium text-[12px]">
              {item}
              <button
                type="button"
                onClick={() => onRemoveItem(item)}
                className="text-red-500 hover:text-red-700 ml-2"
                aria-label={`Remove ${item}`}
              >
                ×
              </button>
              {index < selectedItems.length - 1 && <span className="ml-1"></span>}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

interface CoordinatorUser {
  id: string
  username: string
  email: string
  name?: string
  role?: string
}

interface CoordinatorSelectProps {
  value: string
  onChange: (value: string) => void
  excludeIds?: string[]
  onRemove?: () => void
  canRemove?: boolean
  label: string
  index: number
  formValidationAttempted?: boolean
}

const COORDINATOR_ROLES = ["SHM", "HM", "AGENCY", "SHM_ADMIN", "FBDE_ADMIN"]

const CoordinatorSelect = ({
  value,
  onChange,
  excludeIds = [],
  onRemove,
  canRemove = false,
  label,
  index,
  formValidationAttempted = false,
}: CoordinatorSelectProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const [coordinators, setCoordinators] = useState<CoordinatorUser[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Fetch coordinators on mount and when search changes
  useEffect(() => {
    const fetchCoordinators = async () => {
      setIsLoading(true)
      try {
        const params: any = {
          role: COORDINATOR_ROLES,
          limit: "30",
          page: "1",
          blockStatus: false,
        }
        
        if (searchTerm.trim()) {
          params.search = searchTerm.trim()
        }

        const response = await getAllUsers(params)
        
        if (response?.data?.users) {
          let filtered = response.data.users.filter(
            (user: CoordinatorUser) => !excludeIds.includes(user.id) && user.id !== value
          )
          
          // If we have a selected value and it's not in the filtered list, try to find it
          if (value && !filtered.find((u: CoordinatorUser) => u.id === value)) {
            const selectedUser = response.data.users.find((u: CoordinatorUser) => u.id === value)
            if (selectedUser && !excludeIds.includes(selectedUser.id)) {
              filtered = [selectedUser, ...filtered]
            } else if (value && !selectedUser) {
              // If value exists but not in current results, try to fetch that specific user
              try {
                const userResponse = await getAllUsers({ role: COORDINATOR_ROLES, search: value, limit: "1", page: "1" })
                if (userResponse?.data?.users) {
                  const foundUser = userResponse.data.users.find((u: CoordinatorUser) => u.id === value)
                  if (foundUser && !excludeIds.includes(foundUser.id)) {
                    filtered = [foundUser, ...filtered]
                  }
                }
              } catch (error) {
                console.error("Failed to fetch selected coordinator:", error)
              }
            }
          }
          
          setCoordinators(filtered)
        }
      } catch (error) {
        console.error("Failed to fetch coordinators:", error)
      } finally {
        setIsLoading(false)
      }
    }

    // Debounce search
    if (searchTimeout) {
      clearTimeout(searchTimeout)
    }

    const timeout = setTimeout(() => {
      fetchCoordinators()
    }, searchTerm.trim() ? 300 : 0)

    setSearchTimeout(timeout)

    return () => {
      if (timeout) clearTimeout(timeout)
    }
  }, [searchTerm, excludeIds, value])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setSearchTerm("")
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside)
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isOpen])

  const selectedCoordinator = coordinators.find((c) => c.id === value) || 
    (value ? { id: value, username: "", email: "", name: "" } : null)

  const filteredCoordinators = coordinators.filter(
    (coordinator) =>
      coordinator.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      coordinator.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (coordinator.name && coordinator.name.toLowerCase().includes(searchTerm.toLowerCase()))
  )

  return (
    <div className="w-full space-y-2">
      <Label className="text-sm font-medium text-gray-700">
        {label} <span className="text-red-500">*</span>
      </Label>
      <div className="relative" ref={dropdownRef}>
        <div
          className={`h-12 rounded-lg w-full max-w-[500px] border px-3 py-2 cursor-pointer flex items-center justify-between ${
            formValidationAttempted && !value
              ? "border-red-300 focus:border-red-500"
              : "border-gray-200 focus:border-orange-500"
          }`}
          onClick={() => setIsOpen(!isOpen)}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault()
              setIsOpen(!isOpen)
            }
          }}
        >
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {selectedCoordinator ? (
              <div className="flex items-center gap-2 min-w-0">
                <Avatar className="h-6 w-6">
                  <AvatarFallback className="text-xs">
                    {selectedCoordinator.username?.charAt(0).toUpperCase() || "?"}
                  </AvatarFallback>
                </Avatar>
                <span className="text-gray-900 truncate">
                  {selectedCoordinator.name || selectedCoordinator.username}
                </span>
              </div>
            ) : (
              <span className="text-gray-500">Select coordinator</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {canRemove && onRemove && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  onRemove()
                }}
                className="p-1 hover:bg-red-50 rounded-full text-red-500 hover:text-red-700 transition-colors"
                aria-label="Remove coordinator"
              >
                <X className="h-4 w-4" />
              </button>
            )}
            <Search className="h-4 w-4 text-gray-400 shrink-0" />
          </div>
        </div>

        {isOpen && (
          <div
            className="absolute top-full left-0 right-0 max-w-[500px] bg-white border border-gray-200 rounded-lg shadow-lg z-50 mt-1"
            role="listbox"
          >
            <div className="p-2 border-b">
              <Input
                placeholder="Search coordinators..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-8 text-sm"
                autoFocus
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <div className="max-h-60 overflow-y-auto">
              {isLoading ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-600 mr-2"></div>
                  Loading coordinators...
                </div>
              ) : filteredCoordinators.length > 0 ? (
                filteredCoordinators.map((coordinator) => (
                  <div
                    key={coordinator.id}
                    className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                    role="option"
                    aria-selected={value === coordinator.id}
                    onClick={() => {
                      onChange(coordinator.id)
                      setIsOpen(false)
                      setSearchTerm("")
                    }}
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="text-xs">
                        {coordinator.username?.charAt(0).toUpperCase() || "?"}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm text-gray-900 truncate">
                        {coordinator.name || coordinator.username}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {coordinator.email} - {coordinator.role}
                      </div>
                    </div>
                    {value === coordinator.id && (
                      <div className="h-2 w-2 bg-orange-500 rounded-full shrink-0"></div>
                    )}
                  </div>
                ))
              ) : (
                <div className="px-3 py-4 text-center text-gray-500 text-sm">
                  {searchTerm ? "No coordinators found" : "No coordinators available"}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {formValidationAttempted && !value && (
        <p className="text-red-500 text-xs mt-1" role="alert">Required</p>
      )}
    </div>
  )
}

const validatePincode = (pincode: string): boolean => {
  // Must be exactly 6 digits, no alphabets or special characters
  return /^[0-9]{6}$/.test(pincode);
};

const validateLocationLink = (url: string): boolean => {
  // Basic validation - just check if it's not empty
  return url.trim().length > 0;
};


const isCoordinatorEditor = (role?: string) =>
  Boolean(role && COORDINATOR_ROLES.includes(role))

/** Coordinators editing assigned jobs keep company locked; canPostJobs posters can pick any company. */
const isCompanyFieldLocked = (role?: string, canPostJobs?: boolean) =>
  isCoordinatorEditor(role) && !canPostJobs

const CreateJobPage = () => {
  const {user}=useRbac()
  const navigate = useNavigate()
  const [companies, setCompanies] = useState<Company[]>([])
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false)
  const [companySearchTerm, setCompanySearchTerm] = useState("")
  const [isCompanyDropdownOpen, setIsCompanyDropdownOpen] = useState(false)
  const companyDropdownRef = useRef<HTMLDivElement>(null)
  const [isCreatingJob, setIsCreatingJob] = useState(false)
  const [isLoadingInitial, setIsLoadingInitial] = useState(false)
  const jobLoadingRef = useRef(false)
  const companiesLoadingRef = useRef(false)
  const [formValidationAttempted, setFormValidationAttempted] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [editJobId, setEditJobId] = useState<string>("")
  const [createdJobId, setCreatedJobId] = useState<string>("")
  const [isAiDialogOpen, setIsAiDialogOpen] = useState(false)
  const [aiJobDescription, setAiJobDescription] = useState("")
  const [isAiProcessing, setIsAiProcessing] = useState(false)
  const [categoryOptions, setCategoryOptions] = useState<Array<{label: string, value: string}>>([])
  const [categorySelectedOption, setCategorySelectedOption] = useState<string>("")
  const [customCategory, setCustomCategory] = useState<string>("")
  const [shiftSelectedOption, setShiftSelectedOption] = useState<string>("")
  const [customShift, setCustomShift] = useState<string>("")
  const [descriptionGenerating, setDescriptionGenerating] = useState(false)
  const [keywordsGenerating, setKeywordsGenerating] = useState(false)
  const [forceUpdate, setForceUpdate] = useState(0)

  const [formData, setFormData] = useState<JobData>({
    id: "",
    title: "",
    companyId: "",
    location: "",
    workType: "on-site",
    addressDetails: {
      street: "",
      area: "",
      city: "",
      pincode: "",
    },
    locationLink: "",
    skills: {
      requiredSkills: [],
      preferredSkills: [],
      niceToHaveSkills: [],
    },
    minExperience: 0,
    maxExperience: 0,
    qualificationRequirements: [],
    languageRequirements: [],
    minAge: 18,
    maxAge: 34,
    employmentType: "",
    category: "",
    minSalary: 0,
    maxSalary: 0,
    currency: "INR",
    noOfOpenings: 1,
    shiftTimings: "",
    tenureInDays: 0,
    paymentFrequency: "yearly",
    commissionType: "fixed",
    commissionAmount: 0,
    commissionPercentage: 0,
    hiringNeed: "Immediate",
    description: "",
    aiEnhancedDescription: "",
    suggestedKeywordsForSeo: [],
    visibility: "public",
    status: "",
    isExternal: false,
    coordinators: [], // Initialize with empty array, minimum 1 required
  })


  useEffect(() => {
    console.log("🔧 CreateJobPage component mounted")
    
    const fetchCompanies = async () => {
      if (companiesLoadingRef.current || isLoadingCompanies) {
        console.log("⏳ Already loading companies, skipping...")
        return
      }
      
      companiesLoadingRef.current = true
      setIsLoadingCompanies(true)
      try {
        // Pass isExternal value to filter companies
        const response = await getCompanyListForSelection(formData.isExternal)
        console.log("Companies API response:", response)
        if (response.status === "success" && response.data.companies) {
          console.log("Companies loaded:", response.data.companies)
          setCompanies(response.data.companies)
        }
      } catch (error) {
        console.error("Failed to fetch companies:", error)
        toast.error("Failed to load companies.")
      } finally {
        setIsLoadingCompanies(false)
        companiesLoadingRef.current = false
      }
    }
if(user.role==="COMPANY"){
      console.log("👤 COMPANY user detected, auto-assigning company");
      console.log("   User companyId:", user.companyId);
      
      console.log("   User object:", user);
      
      if (!user.companyId || user.companyId.trim() === "") {
        console.error("❌ ERROR: COMPANY user has no companyId!");
        console.error("   This user needs to complete company onboarding first");
        toast.error("Company profile not found. Please complete your company onboarding before posting jobs.");
      }
      
      setFormData(prev=>({
        ...prev,
        companyId: user.companyId || "" // Use empty string as fallback to prevent undefined
      }))
      console.log("   ✓ Company auto-assigned for COMPANY user");
      return
}
    if (isCompanyFieldLocked(user.role, user.canPostJobs)) {
      return
    }
fetchCompanies()

    
    return () => {
      console.log("🔧 CreateJobPage component unmounted")
    }
  }, [formData.isExternal, user?.role, user?.canPostJobs, user?.companyId])

  const filteredCompanies = useMemo(() => 
    companies.filter(company =>
    company.name.toLowerCase().includes(companySearchTerm.toLowerCase())
    ), [companies, companySearchTerm]
  )

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (companyDropdownRef.current && !companyDropdownRef.current.contains(event.target as Node)) {
        setIsCompanyDropdownOpen(false)
        setCompanySearchTerm("")
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Fetch categories from API
  useEffect(() => {
    const fetchCategoryOptions = async () => {
      try {
        const response = await getJobCategoriesList();
        setCategoryOptions(response.data || []);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
        toast.error('Failed to load categories');
      }
    };
    fetchCategoryOptions();
  }, [])

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const mode = urlParams.get("mode")
    if (
      user?.role &&
      COORDINATOR_ROLES.includes(user.role) &&
      mode !== "edit" &&
      !user?.canPostJobs
    ) {
      navigate("/coordinations", { replace: true })
    }
  }, [user?.role, user?.canPostJobs, navigate])

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const jobId = urlParams.get("jobId")
    const mode = urlParams.get("mode")

    console.log("🔍 Job loading useEffect triggered")
    console.log("Job ID:", jobId)
    console.log("Mode:", mode)
    console.log("jobLoadingRef.current:", jobLoadingRef.current)

    if (jobId && mode === "edit" && !jobLoadingRef.current) {
      console.log("✅ Edit mode detected, loading job details...")
      jobLoadingRef.current = true
      setIsEditMode(true)
      setEditJobId(jobId)
      setCreatedJobId(jobId)

      const fetchJobDetails = async () => {
        if (isLoadingInitial) {
          console.log("⏳ Already loading job details, skipping...")
          return
        }
        
        setIsLoadingInitial(true)
        try {
          const jobDetails = await getJobDetailsById(jobId)
          if (jobDetails.status === "success" && jobDetails.data) {
            const data = jobDetails.data
            console.log("Job details loaded:", data.job)
            console.log("Company info in job:", data.job.company)
            console.log("Company ID in job:", data.job.companyId)

            const newFormData: JobData = {
              id: jobId,
              title: data.job.title || "",
              companyId: (() => {
                if (data.job.companyId) return data.job.companyId;
                if (data.job.company?.id) return data.job.company.id;
                
                if (data.job.company && typeof data.job.company === 'object') {
                  const companyKeys = Object.keys(data.job.company);
                  const idKey = companyKeys.find(key => 
                    key !== 'name' && key !== 'logoUrl' && key.length > 10
                  );
                  if (idKey) return idKey;
                }
                
                return "";
              })(),
              location: data.job.location || "",
              workType: data.job.workType || "on-site",
              addressDetails: {
                street: data.job.addressDetails?.street || "",
                area: data.job.addressDetails?.area || "",
                city: data.job.addressDetails?.city || "",
                pincode: data.job.addressDetails?.pincode || "",
              },
              locationLink: data.job.locationLink || "",
              skills: {
                requiredSkills: data.job.skills?.requiredSkills || [],
                preferredSkills: data.job.skills?.preferredSkills || [],
                niceToHaveSkills: data.job.skills?.niceToHaveSkills || [],
              },
              minExperience: data.job.minExperience || 0,
              maxExperience: data.job.maxExperience || 0,
              qualificationRequirements: data.job.qualificationRequirements || [],
              languageRequirements: data.job.languageRequirements || [],
              minAge: data.job.minAge || 18,
              maxAge: data.job.maxAge || 34,
              employmentType: data.job.employmentType || "",
              category: data.job.category || "",
              minSalary: data.job.minSalary || 0,
              maxSalary: data.job.maxSalary || 0,
              currency: data.job.currency || "INR",
              noOfOpenings: data.job.noOfOpenings || 1,
              shiftTimings: data.job.shiftTimings || "",
              tenureInDays: data.job.tenureInDays || 0,
              paymentFrequency: data.job.paymentFrequency || "yearly",
              commissionType: data.job.commissionType || "fixed",
              commissionAmount: data.job.commissionAmount || 0,
              commissionPercentage: data.job.commissionPercentage || 0,
              hiringNeed: data.job.hiringNeed || "Immediate",
              description: data.job.description || "",
              aiEnhancedDescription: data.job.aiEnhancedDescription || "",
              suggestedKeywordsForSeo: data.job.suggestedKeywordsForSeo || [],
              visibility: data.job.visibility || "public",
              status: data.job.status || "",
              isExternal: data.job.isExternal || false,
              coordinators: (data.job.coordinators || []).map((coord: any) => {
                // Extract ID if it's an object, otherwise use the value as-is
                return typeof coord === 'object' && coord !== null && 'id' in coord ? coord.id : coord;
              }),
            }

            if (data.job.company && data.job.company.name) {
              const companyId = newFormData.companyId;
              if (companyId) {
                setCompanies(prev => {
                  const companyExists = prev.some(c => c.id === companyId);
                  if (!companyExists) {
                    console.log("Adding missing company to list:", {
                      id: companyId,
                      name: data.job.company.name
                    });
                    return [...prev, {
                      id: companyId,
                      name: data.job.company.name,
                      logoUrl: data.job.company.logoUrl
                    }];
                  }
                  return prev;
                });
              }
            }

            setFormData(newFormData)
            setCategorySelectedOption(data.job.category || "")
            setShiftSelectedOption(data.job.shiftTimings || "")

            toast.success("Job data loaded! Continue where you left off.")
          } else {
            console.warn("Job details response invalid:", jobDetails)
            toast.error("Failed to load job details.")
          }
        } catch (error) {
          console.error("Error fetching job details:", error)
          toast.error("Failed to load job details.")
        } finally {
          setIsLoadingInitial(false)
          jobLoadingRef.current = false
        }
      }

      fetchJobDetails()
    } else {
      console.log("🆕 Starting fresh job creation")
    }
  }, [])

  const validateForm = () => {
    const errors: string[] = [];

    // Basic Info Validation
    if (!(formData.title || "").trim()) errors.push("Job title is required");
    
    // Company validation
    if (user.role === "COMPANY") {
      // For COMPANY users, check if companyId was auto-assigned successfully
      if (!formData.companyId || formData.companyId.trim() === "") {
        errors.push("Company profile not found. Please complete your company onboarding first.");
        console.error("❌ COMPANY user has no companyId:", user);
      }
    } else {
      // For other roles, require manual company selection
      if (!(formData.companyId || "").trim()) {
        errors.push("Company is required");
      }
    }
    
    if (!(formData.location || "").trim()) errors.push("Location is required");
    if (!formData.workType) errors.push("Work type is required");
    if (!(formData.addressDetails?.street || "").trim()) errors.push("Street address is required");
    if (!(formData.addressDetails?.area || "").trim()) errors.push("Area is required");
    if (!(formData.addressDetails?.city || "").trim()) errors.push("City is required");
    if (!(formData.addressDetails?.pincode || "").trim()) errors.push("Pincode is required");
    else if (!validatePincode(formData.addressDetails?.pincode || "")) errors.push("Pincode must be exactly 6 digits");
    if (!(formData.locationLink || "").trim()) errors.push("Location link is required");
    else if (!validateLocationLink(formData.locationLink || "")) errors.push("Please enter a valid location link");

    // Requirements Validation
    if (!(formData.skills?.requiredSkills || []).length) errors.push("At least one required skill is needed");
    if (!(formData.skills?.preferredSkills || []).length) errors.push("At least one preferred skill is needed");
    const minExp = typeof formData.minExperience === 'number' ? formData.minExperience : parseInt(formData.minExperience || "0");
    const maxExp = typeof formData.maxExperience === 'number' ? formData.maxExperience : parseInt(formData.maxExperience || "0");
    
    if (minExp < 0) errors.push("Minimum experience cannot be negative");
    if (maxExp < 0) errors.push("Maximum experience cannot be negative");
    if (maxExp < minExp) errors.push("Maximum experience must be greater than or equal to minimum");
    if (!(formData.qualificationRequirements || []).length) errors.push("At least one qualification is required");
    if (!(formData.languageRequirements || []).length) errors.push("At least one language is required");
    if ((formData.minAge || 0) < 18) errors.push("Minimum age must be at least 18");
    if ((formData.maxAge || 0) <= (formData.minAge || 0)) errors.push("Maximum age must be greater than minimum age");

    // Terms Validation
    if (!(formData.employmentType || "").trim()) errors.push("Employment type is required");
    if (!(formData.category || "").trim()) errors.push("Category is required");
    if ((formData.minSalary || 0) <= 0) errors.push("Minimum salary is required");
    if ((formData.maxSalary || 0) <= 0) errors.push("Maximum salary is required");
    if ((formData.maxSalary || 0) < (formData.minSalary || 0)) errors.push("Maximum salary must be greater than or equal to minimum");
    if (!(formData.currency || "").trim()) errors.push("Currency is required");
    if ((formData.noOfOpenings || 0) <= 0) errors.push("Number of openings is required");
    if (!(formData.shiftTimings || "").trim()) errors.push("Shift timings are required");
    
    // Skip tenure and commission validation for external jobs
    if (!formData.isExternal) {
      if ((formData.tenureInDays || 0) <= 0) errors.push("Tenure is required");
      if (!formData.commissionType) errors.push("Commission type is required");
      if ((formData.commissionAmount || 0) < 0) errors.push("Commission amount cannot be negative");
      if (formData.commissionType === "percentage" && (formData.commissionAmount || 0) > 100) errors.push("Commission percentage cannot be greater than 100");
    }
    
    if (!formData.paymentFrequency) errors.push("Payment frequency is required");
    if (!formData.hiringNeed) errors.push("Hiring need is required");

    // Publish Validation
    if (!(formData.description || "").trim()) errors.push("Job description is required");
    if (!(formData.suggestedKeywordsForSeo || []).length) errors.push("At least one SEO keyword is required");
    if (!formData.visibility) errors.push("Visibility is required");

    // Coordinators Validation - not required for COMPANY users or coordinator editors
    if (user.role !== "COMPANY" && !isCoordinatorEditor(user.role)) {
    const validCoordinators = (formData.coordinators || []).filter((coord: any) => {
      if (!coord) return false;
      // Extract ID if it's an object, otherwise use the value as-is
      const id = typeof coord === 'object' && coord !== null && 'id' in coord ? coord.id : coord;
      if (!id) return false;
      const idStr = String(id);
      return idStr.trim() !== "";
    });
    if (validCoordinators.length === 0) errors.push("At least one coordinator is required");
    if (validCoordinators.length > 3) errors.push("Maximum of 3 coordinators allowed");
    }

    return errors;
  }

  const handleSubmit = async () => {
    setFormValidationAttempted(true)
    const errors = validateForm()

    if (errors.length > 0) {
      toast.error(errors[0])
      // Scroll to first error field
      const firstErrorField = document.querySelector('[aria-invalid="true"]')
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' })
        ;(firstErrorField as HTMLElement).focus()
      }
      return
    }

    setIsCreatingJob(true)
    try {
      let jobId = createdJobId
      if (!isEditMode) {
        // const response = await createJobDraft({
        //   title: formData.title ?? "",
        //   companyId: formData.companyId ?? "",
        //   workType: formData.workType ?? "on-site",
        //   location: formData.location ?? "",
        //   addressDetails: formData.addressDetails,
        //   locationLink: formData.locationLink,
        //   skills: formData.skills,
        //   minExperience: typeof formData.minExperience === 'number' ? formData.minExperience : parseInt(formData.minExperience || "0"),
        //  maxExperience: typeof formData.maxExperience === 'number' ? formData.maxExperience : parseInt(formData.maxExperience || "0"),
        // qualificationRequirements: formData.qualificationRequirements,
        // languageRequirements: formData.languageRequirements,
        // minAge: formData.minAge,
        // maxAge: formData.maxAge,
        // employmentType: formData.employmentType,
        // category: formData.category,
        // minSalary: formData.minSalary,
        // maxSalary: formData.maxSalary,
        // currency: formData.currency,
        // noOfOpenings: formData.noOfOpenings,
        // shiftTimings: formData.shiftTimings,
        // tenureInDays: formData.tenureInDays,
        // paymentFrequency: formData.paymentFrequency,
        // commissionType: formData.commissionType,
        // commissionAmount: formData.commissionAmount,
        // commissionPercentage: formData.commissionPercentage,
        // hiringNeed: formData.hiringNeed,
        // })
        // if (response.status === "success" && response.data.jobId) {
        //   jobId = response.data.jobId
        //   setCreatedJobId(jobId)
        //   toast.success("Job draft created successfully!")
        // } else {
        //   throw new Error("Failed to create job draft")
        // }
      }

      // await updateJobRequirements(jobId, {
      //   skills: formData.skills,
      //   minExperience: typeof formData.minExperience === 'number' ? formData.minExperience : parseInt(formData.minExperience || "0"),
      //   maxExperience: typeof formData.maxExperience === 'number' ? formData.maxExperience : parseInt(formData.maxExperience || "0"),
      //   qualificationRequirements: formData.qualificationRequirements,
      //   languageRequirements: formData.languageRequirements,
      //   minAge: formData.minAge,
      //   maxAge: formData.maxAge,
      // })

      // await updateJobTerms(jobId, {
      //   employmentType: formData.employmentType,
      //   category: formData.category,
      //   minSalary: formData.minSalary,
      //   maxSalary: formData.maxSalary,
      //   currency: formData.currency,
      //   noOfOpenings: formData.noOfOpenings,
      //   shiftTimings: formData.shiftTimings,
      //   tenureInDays: formData.tenureInDays,
      //   paymentFrequency: formData.paymentFrequency,
      //   commissionType: formData.commissionType,
      //   commissionAmount: formData.commissionAmount,
      //   commissionPercentage: formData.commissionPercentage,
      //   hiringNeed: formData.hiringNeed,
      // })
      const jobData = {
        description: formData.description,
        aiEnhancedDescription: formData.aiEnhancedDescription,
        suggestedKeywordsForSeo: formData.suggestedKeywordsForSeo,
        visibility: formData.visibility,
        status: "PUBLISHED",
        title: formData.title ?? "",
        companyId: formData.companyId ?? "",
        workType: formData.workType ?? "on-site",
        location: formData.location ?? "",
        addressDetails: formData.addressDetails,
        locationLink: formData.locationLink,
        skills: formData.skills,
        minExperience: typeof formData.minExperience === 'number' ? formData.minExperience : parseInt(formData.minExperience || "0"),
        maxExperience: typeof formData.maxExperience === 'number' ? formData.maxExperience : parseInt(formData.maxExperience || "0"),
        qualificationRequirements: formData.qualificationRequirements,
        languageRequirements: formData.languageRequirements,
        minAge: formData.minAge,
        maxAge: formData.maxAge,
        employmentType: formData.employmentType,
        category: formData.category,
        minSalary: formData.minSalary,
        maxSalary: formData.maxSalary,
        currency: formData.currency,
        noOfOpenings: formData.noOfOpenings,
        shiftTimings: formData.shiftTimings,
        // Use hardcoded values for external jobs
        tenureInDays: formData.isExternal ? 0 : formData.tenureInDays,
        paymentFrequency: formData.paymentFrequency,
        commissionType: formData.isExternal ? "percentage" : formData.commissionType,
        commissionAmount: formData.isExternal ? 0 : formData.commissionAmount,
        commissionPercentage: formData.isExternal ? 0 : formData.commissionPercentage,
        hiringNeed: formData.hiringNeed,
        isExternal: formData.isExternal || false,
        isExternalJob: formData.isExternal || false,
        coordinators: (formData.coordinators || []).filter((coord: any) => {
          if (!coord) return false;
          // Extract ID if it's an object, otherwise use the value as-is
          const id = typeof coord === 'object' && coord !== null && 'id' in coord ? coord.id : coord;
          if (!id) return false;
          const idStr = String(id);
          return idStr.trim() !== "";
        }).map((coord: any) => {
          // Extract ID for the final array
          return typeof coord === 'object' && coord !== null && 'id' in coord ? coord.id : coord;
        }),
      }

      const response = isEditMode ? await editJob(jobId, jobData) : await publishJob(jobData)

      if (response.status === "success" || response.data) {
        const needsApproval = Boolean(user?.canPostJobs)
        toast.success(
          needsApproval
            ? isEditMode
              ? "Job submitted for admin approval!"
              : "Job submitted for admin approval!"
            : isEditMode
              ? "Job updated successfully!"
              : "Job published successfully!"
        )
        setTimeout(() => {
          if (needsApproval) {
            window.location.href = "/dashboard?status=pendingApproval"
          } else {
            window.location.href = isCoordinatorEditor(user.role) ? "/coordinations" : "/dashboard"
          }
        }, 2000)
      }
    } catch (error) {
      console.error("Failed to save/publish job:", error)
      toast.error("Failed to save/publish job. Please try again.")
    } finally {
      setIsCreatingJob(false)
    }
  }

  const handleSaveAsDraft = async () => {
    if (!createdJobId && !isEditMode) {
      try {
       if (!formData.companyId) {
  throw new Error('companyId is required')
}
if (!formData.title) {
  throw new Error('title is required')
}
if (!formData.workType) {
  throw new Error('workType is required')
}
if (!formData.location) {
  throw new Error('location is required')
}

const response = await createJobDraft({
  title: formData.title,
  companyId: formData.companyId,
  workType: formData.workType,
  location: formData.location,
  addressDetails: formData.addressDetails,
  locationLink: formData.locationLink,
})
        if (response.status === "success" && response.data.jobId) {
          setCreatedJobId(response.data.jobId)
        } else {
          throw new Error("Failed to create job draft")
        }
      } catch (error) {
        console.error("Failed to create draft:", error)
        toast.error("Failed to create draft. Please try again.")
        return
      }
    }

    setIsCreatingJob(true)
    try {
      const response = await updateJob(createdJobId || editJobId, {
        ...formData,
        status: "drafted",
      })
      if (response.status === "success" || response.data) {
        toast.success("Draft saved successfully!")
      }
    } catch (error) {
      console.error("Failed to save draft:", error)
      toast.error("Failed to save draft. Please try again.")
    } finally {
      setIsCreatingJob(false)
    }
  }

  const handleClearDraft = () => {
    const shouldClear = window.confirm("Are you sure you want to clear the saved draft? This action cannot be undone.")
    if (shouldClear) {
      setFormData({
        id: "",
        title: "",
        companyId: "",
        location: "",
        workType: "on-site",
        addressDetails: { street: "", area: "", city: "", pincode: "" },
        locationLink: "",
        skills: { requiredSkills: [], preferredSkills: [], niceToHaveSkills: [] },
        minExperience: 0,
        maxExperience: 0,
        qualificationRequirements: [],
        languageRequirements: [],
        minAge: 18,
        maxAge: 34,
        employmentType: "",
        category: "",
        minSalary: 0,
        maxSalary: 0,
        currency: "INR",
        noOfOpenings: 1,
        shiftTimings: "",
        tenureInDays: 0,
        paymentFrequency: "yearly",
        commissionType: "fixed",
        commissionAmount: 0,
        commissionPercentage: 0,
        hiringNeed: "Immediate",
        description: "",
        aiEnhancedDescription: "",
        suggestedKeywordsForSeo: [],
              visibility: "public",
              status: "",
              coordinators: [],
            })
            setCategorySelectedOption("")
            setCustomCategory("")
            setShiftSelectedOption("")
            setCustomShift("")
            setCreatedJobId("")
            setIsEditMode(false)
            setEditJobId("")
            setFormValidationAttempted(false)
            toast.success("Draft cleared! Starting fresh.")
    }
  }

  const handleDeleteJob = async (jobId: string) => {
    if (!jobId) {
      toast.error("No job ID found.")
      return
    }
    const confirmDelete = window.confirm("Are you sure you want to delete this job? This action cannot be undone.")
    if (!confirmDelete) return

    try {
      toast.success("Job deleted successfully!")
      window.location.href = "/dashboard" 
    } catch (error) {
      toast.error("Failed to delete job. Please try again.")
    }
  }

  const handleAiSubmit = async () => {
    if (!aiJobDescription.trim()) {
      toast.error("Please paste your job description")
      return
    }

    setIsAiProcessing(true)
    
    try {
      const prompt = `Parse the following job description and extract the following information in JSON format:
      
      {
        "title": "Job title",
        "location": "Job location",
        "workType": "Must be exactly one of: on-site, remote, hybrid",
        "employmentType": "Must be exactly one of: Full-time, Part-time, Contract, Temporary, Internship",
        "minExperience": "Minimum years of experience (number only)",
        "maxExperience": "Maximum years of experience (number only)",
        "minSalary": "Minimum salary (number only)",
        "maxSalary": "Maximum salary (number only)",
        "category": "Must be exactly one of: Aviation, Banking, Insurance, Oil And Gas, Retail, Education, Consumer Goods, Manufacturing, Information Technology, Health Care, BPO, ITES, Entertainment, Finance, Textile, Media and news, Food processing, Hospitality, Construction, Law, Advertising, Real Estate, Logistics, Government, Non-profit, Sports, Agriculture, Energy, Telecommunications, Automotive, Aerospace, Defense, Consulting, Research, Other",
        "shiftTimings": "Must be exactly one of: Day Shifts, Night Shifts, Evening Shifts, Morning Shifts, Rotating Shifts, Split Shifts, Flexi-shifts, Weekend Shifts, On-call Shifts, Other",
        "requiredSkills": "Array of required technical skills (e.g., ['JavaScript', 'React', 'Node.js'])",
        "preferredSkills": "Array of preferred skills (e.g., ['TypeScript', 'AWS', 'Docker'])",
        "qualification": "Must be exactly one of: 10th, 12th, ITI, Diploma, Graduation (10 + 2 + 3), Graduation (10 + 2 + 4), Post Graduation, PhD",
        "languages": "Array of required languages (e.g., ['English', 'Hindi'])",
        "currency": "Must be exactly one of: INR, USD, EUR",
        "noOfOpenings": "Number of job openings (number only)",
        "tenureInDays": "Job tenure in days (number only)",
        "paymentFrequency": "Must be exactly one of: hourly, daily, weekly, monthly, yearly",
        "commissionType": "Must be exactly one of: fixed, percentage",
        "commissionAmount": "Commission amount (number only)",
        "hiringNeed": "Must be exactly one of: Immediate, Future",
        "visibility": "Must be exactly one of: public, internal",
        "jobDescription": "Full job description including responsibilities, requirements, etc."
      }
      
      Job Description:
      ${aiJobDescription}
      
      IMPORTANT: Return only the JSON object with exact values as specified above.`;

      const response = await generateGeminiContent(prompt)
      console.log("AI Response:", response)
      
      if (response && response.candidates && response.candidates[0]) {
        let parsedData: any;
        try {
          let aiText = response.candidates[0].content.parts[0].text
          // Remove markdown code blocks if present
          if (aiText.includes('```json')) {
            aiText = aiText.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim()
          }
          parsedData = JSON.parse(aiText)
        } catch (parseError) {
          console.error("JSON Parse Error:", parseError)
          console.error("AI Text:", response.candidates[0].content.parts[0].text)
          throw new Error("Invalid JSON response from AI")
        }
        
        console.log("Parsed AI Data:", parsedData)
        
        // Update form data with extracted information
        setFormData(prev => {
          const newData = {
            ...prev,
            title: parsedData.title || prev.title,
            location: parsedData.location || prev.location,
            workType: parsedData.workType || prev.workType,
            employmentType: parsedData.employmentType || prev.employmentType,
            minExperience: parseInt(parsedData.minExperience) || prev.minExperience,
            maxExperience: parseInt(parsedData.maxExperience) || prev.maxExperience,
            minSalary: parseInt(parsedData.minSalary) || prev.minSalary,
            maxSalary: parseInt(parsedData.maxSalary) || prev.maxSalary,
            category: parsedData.category || prev.category,
            shiftTimings: parsedData.shiftTimings || prev.shiftTimings,
            skills: {
              ...prev.skills,
              requiredSkills: parsedData.requiredSkills || prev.skills?.requiredSkills || [],
              preferredSkills: parsedData.preferredSkills || prev.skills?.preferredSkills || []
            },
            qualificationRequirements: parsedData.qualification ? [parsedData.qualification] : prev.qualificationRequirements,
            languageRequirements: parsedData.languages || prev.languageRequirements || [],
            currency: parsedData.currency || prev.currency,
            noOfOpenings: parseInt(parsedData.noOfOpenings) || prev.noOfOpenings,
            tenureInDays: parseInt(parsedData.tenureInDays) || prev.tenureInDays,
            paymentFrequency: parsedData.paymentFrequency || prev.paymentFrequency,
            commissionType: parsedData.commissionType || prev.commissionType,
            commissionAmount: parseFloat(parsedData.commissionAmount) || prev.commissionAmount,
            hiringNeed: parsedData.hiringNeed || prev.hiringNeed,
            visibility: parsedData.visibility || prev.visibility,
            description: parsedData.jobDescription || prev.description
          }
          console.log("Previous form data:", prev)
          console.log("New form data:", newData)
          return newData
        })
        
        // Force re-render of form fields
        setForceUpdate(prev => prev + 1)
        
        setTimeout(() => {
          toast.success("Job description processed and form filled successfully!")
          setIsAiDialogOpen(false)
          setAiJobDescription("")
        }, 500) // Increased delay to ensure state propagation and re-render
      } else {
        console.error("Invalid response structure:", response)
        throw new Error("Invalid response structure from AI")
      }
    } catch (error) {
      console.error("AI processing error:", error)
      toast.error("Failed to process job description. Please try again.")
    } finally {
      setIsAiProcessing(false)
    }
  }

  if (isLoadingInitial) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
        <div className="bg-white rounded-lg p-6 shadow-xl max-w-sm w-full">
          <div className="flex items-center gap-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-600"></div>
            <div>
              <div className="text-gray-900 font-medium">
                {isEditMode ? "Loading job data..." : "Loading..."}
              </div>
              <div className="text-sm text-gray-500">Please wait</div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-orange-50/30 w-full">
      <div className="bg-white/95 backdrop-blur-sm border-b border-gray-100 py-8 px-4 w-full mb-8 sticky top-0 z-40">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between w-full gap-4">
          <div className="flex items-center space-x-6 w-full sm:w-auto">
            <Button
              variant="ghost"
              size="sm"
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              onClick={() => (window.location.href = "/dashboard")}
            >
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </Button>
            <div className="flex-1">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                {isEditMode ? "Edit Job Posting" : "Create New Job"}
              </h1>
              <p className="text-gray-600 mt-1 text-sm">
                {isEditMode
                  ? "Continue editing your job posting"
                  : "Build your perfect job posting in a single form"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4 w-full sm:w-auto justify-end">
            {!isEditMode && (
              <div className="flex items-center gap-2 mr-2">
                {formData.isExternal && (
                  <Label className="text-sm font-medium text-orange-600">
                    External Job
                  </Label>
                )}
                <Switch
                  checked={formData.isExternal || false}
                  onCheckedChange={(checked) => setFormData({ ...formData, isExternal: checked })}
                  className="data-[state=checked]:bg-orange-500"
                />
              </div>
            )}

            {/* buttons not required since multiple stage form submission combined into a single form */}
            {/* <Button
              variant="outline"
              onClick={handleClearDraft}
              className="px-4 py-2 border-gray-200 hover:bg-gray-50"
            >
              Clear Draft
            </Button>
            <Button
              onClick={handleSaveAsDraft}
              disabled={isCreatingJob}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800"
            >
              Save as Draft
            </Button> */}
          </div>
        </div>
      </div>

      <div className="px-4 w-full max-w-7xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm border p-6 sm:p-10 w-full relative">
          {/* AI Assistant Button - Floating in top right of form */}
          {SHOW_JOB_FORM_AI_ASSISTANT && (
          <Dialog open={isAiDialogOpen} onOpenChange={setIsAiDialogOpen}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="absolute top-6 right-6 p-2 hover:bg-orange-50 border-orange-200 hover:border-orange-300 transition-colors z-10"
                title="AI Assistant"
              >
                <Zap className="h-5 w-5 text-orange-600" />
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px]">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold text-gray-900">AI Assistant for Job Posting</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
            <div>
                  <Label htmlFor="ai-job-description" className="text-sm font-medium text-gray-700 mb-2 block">
                    Job Description
                  </Label>
                  <Textarea
                    id="ai-job-description"
                    value={aiJobDescription}
                    onChange={(e) => setAiJobDescription(e.target.value)}
                    placeholder="Paste your job description here..."
                    className="min-h-[200px] resize-none"
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsAiDialogOpen(false)
                      setAiJobDescription("")
                    }}
                    disabled={isAiProcessing}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAiSubmit}
                    disabled={isAiProcessing}
                    className="bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-50"
                  >
                    {isAiProcessing ? "Processing..." : "Submit"}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          )}
          <div className="space-y-16">
            {/* Basic Information */}
                <div>
              <div className="mb-10 pb-4 border-b border-gray-100">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center text-white font-bold text-sm">1</div>
                  <h2 className="text-2xl font-bold text-gray-900">Basic Information</h2>
                </div>
                <p className="text-gray-600 ml-11">Enter the fundamental details about this job position</p>
              </div>
              <div className="space-y-8">
                {/* Job Title and Company Row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="w-full">
                    <Label className="text-sm font-medium text-gray-700 mb-2 block">
                      Job Title <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      key={`title-${forceUpdate}`}
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="e.g. Software Engineer"
                      aria-label="Job title"
                      aria-describedby={formValidationAttempted && !(formData.title || "").trim() ? "title-error" : undefined}
                      className={`h-12 focus:ring-orange-500/20 rounded-lg w-full max-w-[500px] ${
                        formValidationAttempted && !(formData.title || "").trim()
                          ? "border-red-300 focus:border-red-500"
                          : "border-gray-200 focus:border-orange-500"
                      }`}
                    />
                    {formValidationAttempted && !(formData.title || "").trim() && (
                      <p id="title-error" className="text-red-500 text-xs mt-1" role="alert">Required</p>
                    )}
                  </div>
                  {
                    user.role !== "COMPANY" &&
                  <div className="w-full">
                    <Label className="text-sm font-medium text-gray-700 mb-2 block">
                      Company <span className="text-red-500">*</span>
                    </Label>
                    {isCompanyFieldLocked(user.role, user.canPostJobs) ? (
                      <Input
                        value={
                          companies.find((c) => c.id === formData.companyId)?.name ||
                          (formData.companyId ? `Company ID: ${formData.companyId}` : "Company")
                        }
                        readOnly
                        disabled
                        className="h-12 rounded-lg w-full max-w-[500px] border-gray-200 bg-gray-50 text-gray-700"
                      />
                    ) : (
                    <div className="relative" ref={companyDropdownRef}>
                      <div 
                        className={`h-12 rounded-lg w-full max-w-[500px] border px-3 py-2 cursor-pointer flex items-center justify-between ${
                          formValidationAttempted && !formData.companyId
                            ? "border-red-300 focus:border-red-500"
                            : "border-gray-200 focus:border-orange-500"
                        }`}
                        onClick={() => setIsCompanyDropdownOpen(!isCompanyDropdownOpen)}
                        role="combobox"
                        aria-expanded={isCompanyDropdownOpen}
                        aria-haspopup="listbox"
                        aria-label="Select company"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setIsCompanyDropdownOpen(!isCompanyDropdownOpen);
                          }
                        }}
                      >
                        <div className="flex items-center gap-2 flex-1">
                          <span className={formData.companyId ? "text-gray-900" : "text-gray-500"}>
                            {(() => {
                              if (formData.companyId) {
                                const foundCompany = companies.find(c => c.id === formData.companyId);
                                if (foundCompany) {
                                  return foundCompany.name;
                                }
                                return `Company ID: ${formData.companyId}`;
                              }
                              return "Select a company";
                            })()}
                          </span>
                          {(() => {
                            if (formData.companyId) {
                              const foundCompany = companies.find(c => c.id === formData.companyId);
                              if (foundCompany?.isExternalCompany) {
                                return (
                                  <span className="text-xs px-2 py-0.5 bg-orange-100 text-orange-600 rounded-full font-medium">
                                    External
                                  </span>
                                );
                              }
                            }
                            return null;
                          })()}
                        </div>
                        <Search className="h-4 w-4 text-gray-400" />
                      </div>
                      {isCompanyDropdownOpen && (
                        <div 
                          className="absolute top-full left-0 right-0 max-w-[500px] bg-white border border-gray-200 rounded-lg shadow-lg z-50 mt-1"
                          role="listbox"
                          aria-label="Company options"
                        >
                          <div className="p-2">
                            <Input
                              placeholder="Search companies..."
                              value={companySearchTerm}
                              onChange={(e) => setCompanySearchTerm(e.target.value)}
                              className="h-8 text-sm"
                              autoFocus
                            />
                          </div>
                          <div className="max-h-60 overflow-y-auto">
                            {isLoadingCompanies ? (
                              <div className="flex items-center justify-center py-4">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-600 mr-2"></div>
                                Loading companies...
                              </div>
                            ) : filteredCompanies.length > 0 ? (
                              filteredCompanies.map((company) => (
                                <div
                                  key={company.id}
                                  className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                                  role="option"
                                  aria-selected={formData.companyId === company.id}
                                  onClick={() => {
                                    setFormData({ ...formData, companyId: company.id })
                                    setIsCompanyDropdownOpen(false)
                                    setCompanySearchTerm("")
                                  }}
                                >
                                  <Avatar className="h-8 w-8">
                                    <AvatarFallback className="text-xs">{company.name.charAt(0)}</AvatarFallback>
                                  </Avatar>
                                  <div className="flex items-center gap-2 flex-1">
                                    <span className="font-medium text-sm">{company.name}</span>
                                    {company.isExternalCompany && (
                                      <span className="text-xs px-2 py-0.5 bg-orange-100 text-orange-600 rounded-full font-medium">
                                        External
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="px-3 py-4 text-center text-gray-500 text-sm">
                                No companies found
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    )}
                    {formValidationAttempted && !formData.companyId && (
                      <p className="text-red-500 text-xs mt-1" role="alert">Required</p>
                    )}
                  </div>
                  }

                </div>
                {/* Location and Work Type Row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="w-full">
                    <Label className="text-sm font-medium text-gray-700 mb-2 block">
                      Location <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      key={`location-${forceUpdate}`}
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      placeholder="e.g. Remote, New York"
                      className={`h-12 focus:ring-orange-500/20 rounded-lg w-full max-w-[500px] ${
                        formValidationAttempted && !(formData.location || "").trim()
                          ? "border-red-300 focus:border-red-500"
                          : "border-gray-200 focus:border-orange-500"
                      }`}
                    />
                    {formValidationAttempted && !(formData.location || "").trim() && (
                      <p className="text-red-500 text-xs mt-1">Required</p>
                    )}
                  </div>
                  <div className="w-full">
                    <Label className="text-sm font-medium text-gray-700 mb-2 block">
                      Work Type <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      key={`workType-${forceUpdate}`}
                      value={formData.workType}
                      onValueChange={(value) =>
                        setFormData({ ...formData, workType: value as "on-site" | "remote" | "hybrid" })
                      }
                    >
                      <SelectTrigger
                        className={`h-12 rounded-lg w-full max-w-[500px] ${
                          formValidationAttempted && !formData.workType
                            ? "border-red-300 focus:border-red-500"
                            : "border-gray-200 focus:border-orange-500"
                        }`}
                      >
                        <SelectValue placeholder="Select work type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="on-site">On-site</SelectItem>
                        <SelectItem value="remote">Remote</SelectItem>
                        <SelectItem value="hybrid">Hybrid</SelectItem>
                      </SelectContent>
                    </Select>
                    {formValidationAttempted && !formData.workType && (
                      <p className="text-red-500 text-xs mt-1">Required</p>
                    )}
                  </div>
                </div>
                {/* Address Details Row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="w-full">
                    <Label className="text-sm font-medium text-gray-700 mb-2 block">
                      Street Address <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      value={formData.addressDetails?.street || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          addressDetails: { ...formData.addressDetails, street: e.target.value },
                        })
                      }
                      placeholder="e.g. 123 Main St"
                      className={`h-12 focus:ring-orange-500/20 rounded-lg w-full max-w-[500px] ${
                        formValidationAttempted && !(formData.addressDetails?.street || "").trim()
                          ? "border-red-300 focus:border-red-500"
                          : "border-gray-200 focus:border-orange-500"
                      }`}
                    />
                    {formValidationAttempted && !(formData.addressDetails?.street || "").trim() && (
                      <p className="text-red-500 text-xs mt-1">Required</p>
                    )}
                  </div>
                  <div className="w-full">
                    <Label className="text-sm font-medium text-gray-700 mb-2 block">
                      Area/Neighborhood <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      value={formData.addressDetails?.area || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          addressDetails: { ...formData.addressDetails, area: e.target.value },
                        })
                      }
                      placeholder="e.g. Downtown"
                      className={`h-12 focus:ring-orange-500/20 rounded-lg w-full max-w-[500px] ${
                        formValidationAttempted && !(formData.addressDetails?.area || "").trim()
                          ? "border-red-300 focus:border-red-500"
                          : "border-gray-200 focus:border-orange-500"
                      }`}
                    />
                    {formValidationAttempted && !(formData.addressDetails?.area || "").trim() && (
                      <p className="text-red-500 text-xs mt-1">Required</p>
                    )}
                  </div>
                </div>
                {/* City and Pincode Row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="w-full">
                    <Label className="text-sm font-medium text-gray-700 mb-2 block">
                      City <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      value={formData.addressDetails?.city || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          addressDetails: { ...formData.addressDetails, city: e.target.value },
                        })
                      }
                      placeholder="e.g. New York"
                      className={`h-12 focus:ring-orange-500/20 rounded-lg w-full max-w-[500px] ${
                        formValidationAttempted && !(formData.addressDetails?.city || "").trim()
                          ? "border-red-300 focus:border-red-500"
                          : "border-gray-200 focus:border-orange-500"
                      }`}
                    />
                    {formValidationAttempted && !(formData.addressDetails?.city || "").trim() && (
                      <p className="text-red-500 text-xs mt-1">Required</p>
                    )}
                  </div>
                  <div className="w-full">
                    <Label className="text-sm font-medium text-gray-700 mb-2 block">
                      Pincode <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      value={formData.addressDetails?.pincode || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          addressDetails: { ...formData.addressDetails, pincode: e.target.value },
                        })
                      }
                      placeholder="e.g. 123456"
                      className={`h-12 focus:ring-orange-500/20 rounded-lg w-full max-w-[500px] ${
                        formValidationAttempted && 
                        (!(formData.addressDetails?.pincode || "").trim() || 
                         !validatePincode(formData.addressDetails?.pincode || ""))
                          ? "border-red-300 focus:border-red-500"
                          : "border-gray-200 focus:border-orange-500"
                      }`}
                    />
                    {formValidationAttempted && !(formData.addressDetails?.pincode || "").trim() && (
                      <p className="text-red-500 text-xs mt-1">Required</p>
                    )}
                    {formValidationAttempted && 
                      (formData.addressDetails?.pincode || "").trim() && 
                      !validatePincode(formData.addressDetails?.pincode || "") && (
                      <p className="text-red-500 text-xs mt-1">Must be exactly 6 digits</p>
                    )}
                  </div>
                </div>
                <div className="w-full">
                  <Label className="text-sm font-medium text-gray-700 mb-2 block">
                    Location Link <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    value={formData.locationLink}
                    onChange={(e) => setFormData({ ...formData, locationLink: e.target.value })}
                    placeholder="e.g. https://maps.google.com or domain.com"
                    className={`h-12 focus:ring-orange-500/20 rounded-lg w-full max-w-[500px] ${
                      formValidationAttempted && (!(formData.locationLink || "").trim() || !validateLocationLink(formData.locationLink || ""))
                        ? "border-red-300 focus:border-red-500"
                        : "border-gray-200 focus:border-orange-500"
                    }`}
                  />
                  {formValidationAttempted && !(formData.locationLink || "").trim() && (
                    <p className="text-red-500 text-xs mt-1">Required</p>
                  )}
                  {formValidationAttempted && (formData.locationLink || "").trim() && !validateLocationLink(formData.locationLink || "") && (
                    <p className="text-red-500 text-xs mt-1">Please enter a valid location link</p>
                  )}
                </div>
              </div>
            </div>

            {/* Job Requirements */}
            <div>
              <div className="mb-10 pb-4 border-b border-gray-100">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center text-white font-bold text-sm">2</div>
                  <h2 className="text-2xl font-bold text-gray-900">Job Requirements</h2>
                </div>
                <p className="text-gray-600 ml-11">Define the skills, experience, and qualifications needed for this role</p>
              </div>
              <div className="space-y-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="w-full">
                    <MultiSelectWithSuggestions
                      selectedItems={formData.skills?.requiredSkills || []}
                      onAddItem={(item) => {
                        if (!(formData.skills?.requiredSkills || []).includes(item)) {
                          setFormData((prev) => ({
                            ...prev,
                            skills: {
                              ...prev.skills,
                              requiredSkills: [...(prev.skills?.requiredSkills || []), item],
                            },
                          }))
                        }
                      }}
                      onRemoveItem={(item) => {
                        setFormData((prev) => ({
                          ...prev,
                          skills: {
                            ...prev.skills,
                            requiredSkills: (prev.skills?.requiredSkills || []).filter((i) => i !== item),
                          },
                        }))
                      }}
                      suggestions={skillSuggestions}
                      placeholder="Add required skills"
                      label="Required Skills *"
                      excludeItems={[...(formData.skills?.preferredSkills || []), ...(formData.skills?.niceToHaveSkills || [])]}
                    />
                    {formValidationAttempted && !(formData.skills?.requiredSkills || []).length && (
                      <p className="text-red-500 text-xs mt-1">At least one required skill is needed</p>
                    )}
                  </div>
                  <div className="w-full">
                    <MultiSelectWithSuggestions
                      selectedItems={formData.skills?.preferredSkills || []}
                      onAddItem={(item) => {
                        if (!(formData.skills?.preferredSkills || []).includes(item)) {
                          setFormData((prev) => ({
                            ...prev,
                            skills: {
                              ...prev.skills,
                              preferredSkills: [...(prev.skills?.preferredSkills || []), item],
                            },
                          }))
                        }
                      }}
                      onRemoveItem={(item) => {
                        setFormData((prev) => ({
                          ...prev,
                          skills: {
                            ...prev.skills,
                            preferredSkills: (prev.skills?.preferredSkills || []).filter((i) => i !== item),
                          },
                        }))
                      }}
                      suggestions={skillSuggestions}
                      placeholder="Add preferred skills"
                      label="Preferred Skills *"
                      excludeItems={[...(formData.skills?.requiredSkills || []), ...(formData.skills?.niceToHaveSkills || [])]}
                    />
                    {formValidationAttempted && !(formData.skills?.preferredSkills || []).length && (
                      <p className="text-red-500 text-xs mt-1">At least one preferred skill is needed</p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="w-full">
                    <MultiSelectWithSuggestions
                      selectedItems={formData.skills?.niceToHaveSkills || []}
                      onAddItem={(item) => {
                        if (!(formData.skills?.niceToHaveSkills || []).includes(item)) {
                          setFormData((prev) => ({
                            ...prev,
                            skills: {
                              ...prev.skills,
                              niceToHaveSkills: [...(prev.skills?.niceToHaveSkills || []), item],
                            },
                          }))
                        }
                      }}
                      onRemoveItem={(item) => {
                        setFormData((prev) => ({
                          ...prev,
                          skills: {
                            ...prev.skills,
                            niceToHaveSkills: (prev.skills?.niceToHaveSkills || []).filter((i) => i !== item),
                          },
                        }))
                      }}
                      suggestions={skillSuggestions}
                      placeholder="Add nice-to-have skills"
                      label="Nice-to-Have Skills"
                    />
                  </div>
                  <div className="w-full">
                    <Label className="text-sm font-medium text-gray-700 mb-2 block">
                      Total Experience (In years) <span className="text-red-500">*</span>
                    </Label>
                    <div className="grid grid-cols-2 gap-4 max-w-[500px]">
                      <Input
                        type="number"
                        placeholder="Min"
                        value={formData.minExperience ?? ""}
                        onChange={(e) => {
                          const inputValue = e.target.value;
                          if (inputValue === "") {
                          setFormData({
                            ...formData,
                              minExperience: "",
                            })
                          } else {
                            const parsed = Number.parseInt(inputValue);
                            setFormData({
                              ...formData,
                              minExperience: isNaN(parsed) ? 0 : parsed,
                            })
                          }
                        }}
                        className={`h-12 rounded-lg ${
                          formValidationAttempted && (typeof formData.minExperience === 'number' ? formData.minExperience : parseInt(formData.minExperience || "0")) < 0
                            ? "border-red-300 focus:border-red-500"
                            : "border-gray-200 focus:border-orange-500 focus:ring-orange-500/20"
                        }`}
                      />
                      <Input
                        type="number"
                        placeholder="Max"
                        value={formData.maxExperience ?? ""}
                        onChange={(e) => {
                          const inputValue = e.target.value;
                          if (inputValue === "") {
                          setFormData({
                            ...formData,
                              maxExperience: "",
                            })
                          } else {
                            const parsed = Number.parseInt(inputValue);
                            setFormData({
                              ...formData,
                              maxExperience: isNaN(parsed) ? 0 : parsed,
                            })
                          }
                        }}
                        className={`h-12 rounded-lg ${
                          formValidationAttempted &&
                          ((typeof formData.maxExperience === 'number' ? formData.maxExperience : parseInt(formData.maxExperience || "0")) < 0 || 
                           (typeof formData.maxExperience === 'number' ? formData.maxExperience : parseInt(formData.maxExperience || "0")) < 
                           (typeof formData.minExperience === 'number' ? formData.minExperience : parseInt(formData.minExperience || "0")))
                            ? "border-red-300 focus:border-red-500"
                            : "border-gray-200 focus:border-orange-500 focus:ring-orange-500/20"
                        }`}
                      />
                    </div>
                    {formValidationAttempted && (typeof formData.minExperience === 'number' ? formData.minExperience : parseInt(formData.minExperience || "0")) < 0 && (
                      <p className="text-red-500 text-xs mt-1">Minimum experience cannot be negative</p>
                    )}
                    {formValidationAttempted && (typeof formData.maxExperience === 'number' ? formData.maxExperience : parseInt(formData.maxExperience || "0")) < 0 && (
                      <p className="text-red-500 text-xs mt-1">Maximum experience cannot be negative</p>
                    )}
                    {formValidationAttempted && (typeof formData.maxExperience === 'number' ? formData.maxExperience : parseInt(formData.maxExperience || "0")) < (typeof formData.minExperience === 'number' ? formData.minExperience : parseInt(formData.minExperience || "0")) && (
                      <p className="text-red-500 text-xs mt-1">
                        Maximum experience must be greater than or equal to minimum
                      </p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="w-full">
                    <Label className="text-sm font-medium text-gray-700 mb-2 block">
                      Educational Qualification <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={(formData.qualificationRequirements || [])[0] || ""}
                      onValueChange={(value) => setFormData({
                        ...formData,
                        qualificationRequirements: [value]
                      })}
                    >
                      <SelectTrigger
                        className={`h-12 rounded-lg w-full max-w-[500px] ${
                          formValidationAttempted && !(formData.qualificationRequirements || []).length
                            ? "border-red-300 focus:border-red-500"
                            : "border-gray-200 focus:border-orange-500"
                        }`}
                      >
                        <SelectValue placeholder="Select qualification" />
                      </SelectTrigger>
                      <SelectContent>
                        {qualificationSuggestions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {(formData.qualificationRequirements || []).length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {(formData.qualificationRequirements || []).map((item, index) => (
                          <span key={index} className="gap-2 py-2 px-4 bg-orange-50 text-orange-700 border border-orange-200 rounded-xl font-medium">
                            {item}
                            <button
                              type="button"
                              onClick={() => setFormData({
                                ...formData,
                                qualificationRequirements: (formData.qualificationRequirements || []).filter((i) => i !== item)
                              })}
                              className="text-red-500 hover:text-red-700 ml-2"
                              aria-label={`Remove ${item}`}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                    {formValidationAttempted && !(formData.qualificationRequirements || []).length && (
                      <p className="text-red-500 text-xs mt-1">At least one qualification is required</p>
                    )}
                  </div>
                  <div className="w-full">
                    <MultiSelectWithSuggestions
                      selectedItems={formData.languageRequirements || []}
                      onAddItem={(item) => {
                        if (!(formData.languageRequirements || []).includes(item)) {
                          setFormData((prev) => ({
                            ...prev,
                            languageRequirements: [...(prev.languageRequirements || []), item],
                          }))
                        }
                      }}
                      onRemoveItem={(item) => {
                        setFormData((prev) => ({
                          ...prev,
                          languageRequirements: (prev.languageRequirements || []).filter((i) => i !== item),
                        }))
                      }}
                      suggestions={languageSuggestions}
                      placeholder="Add required languages"
                      label="Language Requirements *"
                    />
                    {formValidationAttempted && !(formData.languageRequirements || []).length && (
                      <p className="text-red-500 text-xs mt-1">At least one language is required</p>
                    )}
                  </div>
                </div>
                <div className="w-full">
                  <Label className="text-sm font-medium text-gray-700 mb-2 block">
                    Age Range <span className="text-red-500">*</span>
                  </Label>
                  <div className="grid grid-cols-2 gap-4 max-w-[500px]">
                    <Input
                      type="number"
                      placeholder="Min Age (18)"
                      value={formData.minAge || ""}
                      onChange={(e) => {
                        const value = e.target.value === "" ? 0 : parseInt(e.target.value) || 0;
                        setFormData({ 
                          ...formData, 
                          minAge: value
                        })
                      }}
                      onBlur={(e) => {
                        const value = parseInt(e.target.value) || 0;
                        if (value > 0 && value < 18) {
                          setFormData({ 
                            ...formData, 
                            minAge: 18
                          })
                        } else if (value === 0) {
                          setFormData({ 
                            ...formData, 
                            minAge: 18
                          })
                        }
                      }}
                      min="18"
                      className={`h-12 rounded-lg ${
                        formValidationAttempted && (formData.minAge || 0) < 18
                          ? "border-red-300 focus:border-red-500"
                          : "border-gray-200 focus:border-orange-500 focus:ring-orange-500/20"
                      }`}
                    />
                    <Input
                      type="number"
                      placeholder="Max Age (34)"
                      value={formData.maxAge || ""}
                      onChange={(e) => {
                        const value = e.target.value === "" ? 0 : parseInt(e.target.value) || 0;
                        setFormData({ 
                          ...formData, 
                          maxAge: value
                        })
                      }}
                      onBlur={(e) => {
                        const value = parseInt(e.target.value) || 0;
                        if (value > 0 && value <= (formData.minAge || 0)) {
                          setFormData({ 
                            ...formData, 
                            maxAge: (formData.minAge || 0) + 1
                          })
                        } else if (value === 0) {
                          setFormData({ 
                            ...formData, 
                            maxAge: 34
                          })
                        }
                      }}
                      min="19"
                      className={`h-12 rounded-lg ${
                        formValidationAttempted && (formData.maxAge || 0) <= (formData.minAge || 0)
                          ? "border-red-300 focus:border-red-500"
                          : "border-gray-200 focus:border-orange-500 focus:ring-orange-500/20"
                      }`}
                    />
                  </div>
                  {formValidationAttempted && (formData.minAge || 0) < 18 && (
                    <p className="text-red-500 text-xs mt-1">Minimum age must be at least 18</p>
                  )}
                  {formValidationAttempted && (formData.maxAge || 0) <= (formData.minAge || 0) && (
                    <p className="text-red-500 text-xs mt-1">Maximum age must be greater than minimum age</p>
                  )}
                </div>
              </div>
            </div>

            {/* Job Terms */}
            <div>
              <div className="mb-10 pb-4 border-b border-gray-100">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center text-white font-bold text-sm">3</div>
                  <h2 className="text-2xl font-bold text-gray-900">Job Terms & Compensation</h2>
                </div>
                <p className="text-gray-600 ml-11">Set the employment details, salary, and commission structure</p>
              </div>
              <div className="space-y-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="w-full">
                    <Label className="text-sm font-medium text-gray-700 mb-2 block">
                      Employment Type <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      key={`employmentType-${forceUpdate}`}
                      value={formData.employmentType}
                      onValueChange={(value) => setFormData({ ...formData, employmentType: value })}
                    >
                      <SelectTrigger
                        className={`h-12 rounded-lg w-full max-w-[500px] ${
                          formValidationAttempted && !formData.employmentType
                            ? "border-red-300 focus:border-red-500"
                            : "border-gray-200 focus:border-orange-500"
                        }`}
                      >
                        <SelectValue placeholder="Select employment type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Full-time">Full-time</SelectItem>
                        <SelectItem value="Part-time">Part-time</SelectItem>
                        <SelectItem value="Contract">Contract</SelectItem>
                        <SelectItem value="Temporary">Temporary</SelectItem>
                        <SelectItem value="Internship">Internship</SelectItem>
                      </SelectContent>
                    </Select>
                    {formValidationAttempted && !formData.employmentType && (
                      <p className="text-red-500 text-xs mt-1">Required</p>
                    )}
                  </div>
                  <div className="w-full">
                    <Label className="text-sm font-medium text-gray-700 mb-2 block">
                      Category <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      key={`category-${forceUpdate}`}
                      value={categorySelectedOption || formData.category}
                      onValueChange={(value) => {
                        setCategorySelectedOption(value)
                        // Check if the selected value is "Other" (by checking if it exists in categoryOptions)
                        const isOtherOption = value === 'Other' || !categoryOptions.some(cat => cat.value === value)
                        if (isOtherOption) {
                          setFormData({ ...formData, category: customCategory || '' })
                        } else {
                          setCustomCategory('')
                          setFormData({ ...formData, category: value })
                        }
                      }}
                    >
                      <SelectTrigger
                        className={`h-12 rounded-lg w-full max-w-[500px] ${
                          formValidationAttempted && !formData.category
                            ? "border-red-300 focus:border-red-500"
                            : "border-gray-200 focus:border-orange-500"
                        }`}
                      >
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categoryOptions.map(category => (
                          <SelectItem key={category.value} value={category.value}>
                            {category.label}
                          </SelectItem>
                        ))}
                        {/* Add "Other" option if not already in the list */}
                        {!categoryOptions.some(cat => cat.value === 'Other' || cat.label === 'Other') && (
                          <SelectItem value="Other">Other</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    {(categorySelectedOption === 'Other' || (!categoryOptions.some(cat => cat.value === categorySelectedOption) && categorySelectedOption)) && (
                      <div className="mt-2">
                        <Input
                          value={customCategory}
                          onChange={(e) => {
                            setCustomCategory(e.target.value)
                            setFormData({ ...formData, category: e.target.value })
                          }}
                          placeholder="Enter custom category"
                          className={`h-10 w-full max-w-[500px] ${
                            formValidationAttempted && !formData.category
                              ? 'border-red-300 focus:border-red-500'
                              : 'border-gray-200 focus:border-orange-500'
                          }`}
                        />
                      </div>
                    )}
                    {formValidationAttempted && !formData.category && (
                      <p className="text-red-500 text-xs mt-1">Required</p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="w-full">
                    <Label className="text-sm font-medium text-gray-700 mb-2 block">
                      Currency <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={formData.currency}
                      onValueChange={(value) => setFormData({ ...formData, currency: value })}
                    >
                      <SelectTrigger
                        className={`h-12 rounded-lg w-full max-w-[500px] ${
                          formValidationAttempted && !formData.currency
                            ? "border-red-300 focus:border-red-500"
                            : "border-gray-200 focus:border-orange-500"
                        }`}
                      >
                        <SelectValue placeholder="Select currency" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="INR">INR (₹)</SelectItem>
                        <SelectItem value="USD">USD ($)</SelectItem>
                        <SelectItem value="EUR">EUR (€)</SelectItem>
                      </SelectContent>
                    </Select>
                    {formValidationAttempted && !formData.currency && (
                      <p className="text-red-500 text-xs mt-1">Required</p>
                    )}
                  </div>
                  <div className="w-full">
                    <Label className="text-sm font-medium text-gray-700 mb-2 block">
                      Salary Range <span className="text-red-500">*</span>
                    </Label>
                    <div className="grid grid-cols-2 gap-4 max-w-[500px]">
                      <Input
                        type="number"
                        placeholder="Min"
                        value={formData.minSalary || ""}
                        onChange={(e) => {
                          const value = Number.parseInt(e.target.value) || 0;
                          setFormData({ ...formData, minSalary: value })
                        }}
                        className={`h-12 focus:ring-orange-500/20 rounded-lg ${
                          formValidationAttempted && (formData.minSalary || 0) <= 0
                            ? "border-red-300 focus:border-red-500"
                            : "border-gray-200 focus:border-orange-500"
                        }`}
                      />
                      <Input
                        type="number"
                        placeholder="Max"
                        value={formData.maxSalary || ""}
                        onChange={(e) => {
                          const value = Number.parseInt(e.target.value) || 0;
                          setFormData({ ...formData, maxSalary: value })
                        }}
                        className={`h-12 focus:ring-orange-500/20 rounded-lg ${
                          formValidationAttempted && 
                          ((formData.maxSalary || 0) <= 0 || (formData.maxSalary || 0) < (formData.minSalary || 0))
                            ? "border-red-300 focus:border-red-500"
                            : "border-gray-200 focus:border-orange-500"
                        }`}
                      />
                    </div>
                    {formValidationAttempted && (formData.minSalary || 0) <= 0 && (
                      <p className="text-red-500 text-xs mt-1">Required</p>
                    )}
                    {formValidationAttempted && (formData.maxSalary || 0) <= 0 && (
                      <p className="text-red-500 text-xs mt-1">Required</p>
                    )}
                    {formValidationAttempted && (formData.maxSalary || 0) < (formData.minSalary || 0) && (
                      <p className="text-red-500 text-xs mt-1">Must be greater than min</p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="w-full">
                    <Label className="text-sm font-medium text-gray-700 mb-2 block">
                      Number of Openings <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.noOfOpenings || ""}
                      onChange={(e) => {
                        const value = e.target.value === "" ? 0 : Number.parseInt(e.target.value) || 1;
                        setFormData({ ...formData, noOfOpenings: value })
                      }}
                      className={`h-12 focus:ring-orange-500/20 rounded-lg w-full max-w-[500px] ${
                        formValidationAttempted && (formData.noOfOpenings || 0) <= 0
                          ? "border-red-300 focus:border-red-500"
                          : "border-gray-200 focus:border-orange-500"
                      }`}
                      placeholder="0"
                    />
                    {formValidationAttempted && (formData.noOfOpenings || 0) <= 0 && (
                      <p className="text-red-500 text-xs mt-1">Required</p>
                    )}
                  </div>
                  <div className="w-full">
                    <Label className="text-sm font-medium text-gray-700 mb-2 block">
                      Shift Timings <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      key={`shiftTimings-${forceUpdate}`}
                      value={shiftSelectedOption || formData.shiftTimings}
                      onValueChange={(value) => {
                        setShiftSelectedOption(value)
                        if (value === 'Other') {
                          setFormData({ ...formData, shiftTimings: customShift || '' })
                        } else {
                          setCustomShift('')
                          setFormData({ ...formData, shiftTimings: value })
                        }
                      }}
                    >
                      <SelectTrigger
                        className={`h-12 rounded-lg w-full max-w-[500px] ${
                          formValidationAttempted && !formData.shiftTimings
                            ? "border-red-300 focus:border-red-500"
                            : "border-gray-200 focus:border-orange-500"
                        }`}
                      >
                        <SelectValue placeholder="Select shift timing" />
                      </SelectTrigger>
                      <SelectContent>
                        {shiftTypeOptions.map(option => (
                          <SelectItem key={option} value={option}>{option}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {shiftSelectedOption === 'Other' && (
                      <div className="mt-2">
                        <Input
                          value={customShift}
                          onChange={(e) => {
                            setCustomShift(e.target.value)
                            setFormData({ ...formData, shiftTimings: e.target.value })
                          }}
                          placeholder="Enter custom shift timing"
                          className={`h-10 w-full max-w-[500px] ${
                            formValidationAttempted && !formData.shiftTimings
                              ? 'border-red-300 focus:border-red-500'
                              : 'border-gray-200 focus:border-orange-500'
                          }`}
                        />
                      </div>
                    )}
                    {formValidationAttempted && !formData.shiftTimings && (
                      <p className="text-red-500 text-xs mt-1">Required</p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {!formData.isExternal && (
                    <div className="w-full">
                      <Label className="text-sm font-medium text-gray-700 mb-2 block">
                        Tenure (Days) <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.tenureInDays || ""}
                        onChange={(e) => {
                          const value = Number.parseInt(e.target.value) || 0;
                          setFormData({ ...formData, tenureInDays: value })
                        }}
                        className={`h-12 focus:ring-orange-500/20 rounded-lg w-full max-w-[500px] ${
                          formValidationAttempted && (formData.tenureInDays || 0) <= 0
                            ? "border-red-300 focus:border-red-500"
                            : "border-gray-200 focus:border-orange-500"
                        }`}
                        placeholder="0"
                      />
                      {formValidationAttempted && (formData.tenureInDays || 0) <= 0 && (
                        <p className="text-red-500 text-xs mt-1">Required</p>
                      )}
                    </div>
                  )}
                  <div className="w-full">
                    <Label className="text-sm font-medium text-gray-700 mb-2 block">
                      Payment Frequency <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={formData.paymentFrequency}
                      onValueChange={(value) =>
                        setFormData({
                          ...formData,
                          paymentFrequency: value as "yearly" | "monthly" | "daily" | "weekly" | "hourly",
                        })
                      }
                    >
                      <SelectTrigger
                        className={`h-12 rounded-lg w-full max-w-[500px] ${
                          formValidationAttempted && !formData.paymentFrequency
                            ? "border-red-300 focus:border-red-500"
                            : "border-gray-200 focus:border-orange-500"
                        }`}
                      >
                        <SelectValue placeholder="Select payment frequency" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hourly">Hourly</SelectItem>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="yearly">Yearly</SelectItem>
                      </SelectContent>
                    </Select>
                    {formValidationAttempted && !formData.paymentFrequency && (
                      <p className="text-red-500 text-xs mt-1">Required</p>
                    )}
                  </div>
                </div>
                {!formData.isExternal && (
                  <div className="w-full">
                    <Label className="text-sm font-medium text-gray-700 mb-2 block">
                      Commission <span className="text-red-500">*</span>
                    </Label>
                    <div className="grid grid-cols-2 gap-4 max-w-[500px]">
                      <Select
                        value={formData.commissionType}
                        onValueChange={(value) =>
                          setFormData({ ...formData, commissionType: value as "fixed" | "percentage" })
                        }
                      >
                        <SelectTrigger
                          className={`h-12 rounded-lg ${
                            formValidationAttempted && !formData.commissionType
                              ? "border-red-300 focus:border-red-500"
                              : "border-gray-200 focus:border-orange-500"
                          }`}
                        >
                          <SelectValue placeholder="Type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="fixed">Fixed</SelectItem>
                          <SelectItem value="percentage">Percentage</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.commissionAmount || ""}
                        onChange={(e) => {
                          let value = parseFloat(e.target.value) || 0;
                          if (formData.commissionType === "percentage" && value > 100) {
                            value = 100;
                          }
                          setFormData({ ...formData, commissionAmount: value })
                        }}
                        placeholder={formData.commissionType === "percentage" ? "Percentage" : "Amount"}
                        className={`h-12 focus:ring-orange-500/20 rounded-lg ${
                          formValidationAttempted && ((formData.commissionAmount || 0) < 0 || (formData.commissionType === "percentage" && (formData.commissionAmount || 0) > 100))
                            ? "border-red-300 focus:border-red-500"
                            : "border-gray-200 focus:border-orange-500"
                        }`}
                      />
                    </div>
                    {formValidationAttempted && !formData.commissionType && (
                      <p className="text-red-500 text-xs mt-1">Required</p>
                    )}
                    {formValidationAttempted && (formData.commissionAmount || 0) < 0 && (
                      <p className="text-red-500 text-xs mt-1">Cannot be negative</p>
                    )}
                    {formValidationAttempted && formData.commissionType === "percentage" && (formData.commissionAmount || 0) > 100 && (
                      <p className="text-red-500 text-xs mt-1">Percentage cannot be greater than 100</p>
                    )}
                  </div>
                )}
                <div className="w-full">
                  <Label className="text-sm font-medium text-gray-700 mb-2 block">
                    Hiring Need <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.hiringNeed}
                    onValueChange={(value) =>
                      setFormData({ ...formData, hiringNeed: value as "Immediate" | "Future" })
                    }
                  >
                    <SelectTrigger
                      className={`h-12 rounded-lg w-full max-w-[500px] ${
                        formValidationAttempted && !formData.hiringNeed
                          ? "border-red-300 focus:border-red-500"
                          : "border-gray-200 focus:border-orange-500"
                      }`}
                    >
                      <SelectValue placeholder="Select hiring need" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Immediate">Immediate</SelectItem>
                      <SelectItem value="Future">Future</SelectItem>
                    </SelectContent>
                  </Select>
                  {formValidationAttempted && !formData.hiringNeed && (
                    <p className="text-red-500 text-xs mt-1">Required</p>
                  )}
                </div>
              </div>
            </div>

            {/* Publish Job */}
            <div>
              <div className="mb-10 pb-4 border-b border-gray-100">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center text-white font-bold text-sm">4</div>
                  <h2 className="text-2xl font-bold text-gray-900">Job Description & Publishing</h2>
                </div>
                <p className="text-gray-600 ml-11">Create a compelling job description and set visibility preferences</p>
              </div>
              <div className="space-y-6">
                <div className="w-full mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-sm font-medium text-gray-700">
                      Description <span className="text-red-500">*</span>
                    </Label>
                    <Button
                      type="button"
                      onClick={async () => {
                        if (!formData.title) {
                          toast.error("Please enter a job title first")
                          return
                        }
                        setDescriptionGenerating(true)
                        try {
                          const prompt = `Write a professional job description for the position: ${formData.title}.
The job description must include the following sections with clear headings:
Responsibilities
Requirements
Qualifications

Formatting instructions:
- Use plain section headings on separate lines (no stars, no markdown).
- Under each section, provide bullet points using line breaks and hyphens (-).
- Each bullet point should be concise and start on a new line.
- Do not merge bullets into one paragraph.
- Do not include company information or benefits.
- The output should be clean, structured, and easy to paste into a text editor.`

                          const response = await generateGeminiContent(prompt)
                          const generatedText = response?.candidates?.[0]?.content?.parts?.[0]?.text || ''
                          if (generatedText) {
                            setFormData({ ...formData, description: generatedText })
                            toast.success("Job description generated successfully!")
                          }
                        } catch (error) {
                          console.error("Failed to generate description:", error)
                          toast.error("Failed to generate description. Please try again.")
                        } finally {
                          setDescriptionGenerating(false)
                        }
                      }}
                      className="inline-flex items-center px-3 py-1.5 text-sm bg-orange-100 text-orange-600 rounded-full hover:bg-orange-200 transition-all duration-200"
                    >
                      {descriptionGenerating ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-600 mr-2"></div>
                          Generating...
                        </>
                      ) : (
                        <>
                          <Zap className="w-4 h-4 mr-1.5" />
                          Generate with AI
                        </>
                      )}
                    </Button>
                  </div>
                  <ReactQuill
                    key={`description-${forceUpdate}`}
                    value={formData.description}
                    onChange={(value) => setFormData({ ...formData, description: value })}
                    placeholder="Write a detailed job description"
                    className="rounded-lg w-full h-80 max-w-[1300px]"
                    theme="snow"
                    modules={{
                      toolbar: [
                        [{ 'header': [1, 2, 3, false] }],
                        ['bold', 'italic', 'underline'],
                        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                        ['link'],
                        ['clean']
                      ],
                      clipboard: {
                        matchVisual: false,
                      }
                    }}
                    formats={[
                      'header',
                      'bold', 'italic', 'underline',
                      'list', 'bullet',
                      'link'
                    ]}
                  />
                  {formValidationAttempted && !formData.description && (
                    <p className="text-red-500 text-xs mt-1">Required</p>
                  )}
                </div>
                <div className="flex gap-8 items-start" style={{ marginTop: "90px" }}>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2 max-w-[500px]">
                      <Label className="text-sm font-medium text-gray-700">
                        SEO Keywords <span className="text-red-500">*</span>
                      </Label>
                      <Button
                        type="button"
                        onClick={async () => {
                          if (!formData.title) {
                            toast.error("Please enter a job title first")
                            return
                          }
                          if (!formData.description) {
                            toast.error("Please enter a job description.")
                            return
                          }
                          setKeywordsGenerating(true)
                          try {
                            const prompt = `Generate 20 google relevant SEO keywords for ${formData.title} job posting, here is the description: ${formData.description}. The keywords should be separated by commas. Include both technical and general terms.`
                            const response = await generateGeminiContent(prompt)
                            const generatedText = response?.candidates?.[0]?.content?.parts?.[0]?.text || ''
                            if (generatedText) {
                              const keywords = generatedText
                                .split(',')
                                .map((k: string) => k.trim())
                                .filter((k: string) => k.length > 0)
                              setFormData(prev => ({
                                ...prev,
                                suggestedKeywordsForSeo: [...new Set([...(prev.suggestedKeywordsForSeo || []), ...keywords])]
                              }))
                              toast.success("Keywords generated successfully!")
                            }
                          } catch (error) {
                            console.error("Failed to generate keywords:", error)
                            toast.error("Failed to generate keywords. Please try again.")
                          } finally {
                            setKeywordsGenerating(false)
                          }
                        }}
                        className="inline-flex items-center px-3 py-1.5 text-sm bg-orange-100 text-orange-600 rounded-full hover:bg-orange-200 transition-all duration-200"
                      >
                        {keywordsGenerating ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-600 mr-2"></div>
                            Generating...
                          </>
                        ) : (
                          <>
                            <Zap className="w-4 h-4 mr-1.5" />
                            Generate with AI
                          </>
                        )}
                      </Button>
                    </div>
                    <MultiSelectWithSuggestions
                      selectedItems={formData.suggestedKeywordsForSeo || []}
                      onAddItem={(item) => {
                        setFormData(prev => {
                          if (!(prev.suggestedKeywordsForSeo || []).includes(item)) {
                            return {
                              ...prev,
                              suggestedKeywordsForSeo: [...(prev.suggestedKeywordsForSeo || []), item],
                            }
                          }
                          return prev
                        })
                      }}
                      onRemoveItem={(item) => {
                        setFormData({
                          ...formData,
                          suggestedKeywordsForSeo: (formData.suggestedKeywordsForSeo || []).filter((k) => k !== item),
                        })
                      }}
                      suggestions={[]}
                      placeholder="Add SEO keywords (comma-separated)"
                      label=""
                    />
                    {formValidationAttempted && !(formData.suggestedKeywordsForSeo || []).length && (
                      <p className="text-red-500 text-xs mt-1">At least one SEO keyword is required</p>
                    )}
                  </div>
                  {
                    user.role !=="COMPANY" &&(
                  <div className="flex-1 mt-2">
                    <Label className="text-sm font-medium text-gray-700 mb-2 block">
                      Visibility <span className="text-red-500">*</span>
                    </Label>
                    <div className="flex items-center">
                      <div className="flex rounded-lg overflow-hidden border border-gray-200">
                        <button
                          type="button"
                          className={`px-6 py-2 font-medium focus:outline-none transition-colors duration-150 ${
                            formData.visibility === 'public' ? 'bg-orange-500 text-white font-bold' : 'bg-gray-100 text-gray-700'
                          }`}
                          onClick={() => setFormData({ ...formData, visibility: 'public' })}
                        >
                          Public
                        </button>
                        <button
                          type="button"
                          className={`px-6 py-2 font-medium focus:outline-none transition-colors duration-150 ${
                            formData.visibility === 'internal' ? 'bg-orange-500 text-white font-bold' : 'bg-gray-100 text-gray-700'
                          }`}
                          onClick={() => setFormData({ ...formData, visibility: 'internal' })}
                        >
                          Private
                        </button>
                      </div>
                    </div>
                    {formValidationAttempted && !formData.visibility && (
                      <p className="text-red-500 text-xs mt-1">Required</p>
                    )}
                  </div>
                   )
                  }
                </div>
              </div>
            </div>

            {/* Coordinators Section - admin only (coordinator editors cannot reassign) */}
            {user.role !== "COMPANY" && !isCoordinatorEditor(user.role) && (
            <div>
              <div className="mb-10 pb-4 border-b border-gray-100">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center text-white font-bold text-sm">5</div>
                  <h2 className="text-2xl font-bold text-gray-900">Assign Coordinators</h2>
                </div>
                <p className="text-gray-600 ml-11">Assign SHM, HM, AGENCY, SHM_ADMIN, or FBDE_ADMIN as coordinators to manage this job posting (Minimum 1, Maximum 3)</p>
              </div>
              <div className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  {(formData.coordinators || []).map((coordinatorId: any, index) => {
                    // Extract ID if it's an object, otherwise use the value as-is
                    const coordinatorValue = typeof coordinatorId === 'object' && coordinatorId !== null && 'id' in coordinatorId
                      ? (coordinatorId as any).id 
                      : coordinatorId;
                    return (
                    <CoordinatorSelect
                      key={index}
                      value={coordinatorValue || ""}
                      onChange={(newValue) => {
                        const updated = [...(formData.coordinators || [])]
                        updated[index] = newValue
                        setFormData({ ...formData, coordinators: updated })
                      }}
                      excludeIds={(formData.coordinators || []).filter((coord: any, idx) => {
                        if (idx === index) return false;
                        const id = typeof coord === 'object' && coord !== null && 'id' in coord ? (coord as any).id : coord;
                        return id;
                      }).map((coord: any) => {
                        const id = typeof coord === 'object' && coord !== null && 'id' in coord ? (coord as any).id : coord;
                        return String(id);
                      })}
                      onRemove={
                        (formData.coordinators || []).length > 1
                          ? () => {
                              const updated = (formData.coordinators || []).filter((_, idx) => idx !== index)
                              setFormData({ ...formData, coordinators: updated })
                            }
                          : undefined
                      }
                      canRemove={(formData.coordinators || []).length > 1}
                      label={`Coordinator ${index + 1}`}
                      index={index}
                      formValidationAttempted={formValidationAttempted}
                    />
                    );
                  })}
                  {(formData.coordinators || []).length === 0 && (
                    <CoordinatorSelect
                      key={0}
                      value=""
                      onChange={(newValue) => {
                        setFormData({ ...formData, coordinators: [newValue] })
                      }}
                      excludeIds={[]}
                      label="Coordinator 1"
                      index={0}
                      formValidationAttempted={formValidationAttempted}
                    />
                  )}
                </div>
                {(formData.coordinators || []).length < 3 && (formData.coordinators || []).length > 0 && (
                  <div className="flex justify-start">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setFormData({
                          ...formData,
                          coordinators: [...(formData.coordinators || []), ""],
                        })
                      }}
                      className="flex items-center gap-2 h-10 px-4 border-orange-300 text-orange-600 hover:bg-orange-50"
                    >
                      <Plus className="h-4 w-4" />
                      Add Another Coordinator
                    </Button>
                  </div>
                )}
                {formValidationAttempted && (!formData.coordinators || (formData.coordinators || []).filter((coord: any) => {
                  if (!coord) return false;
                  const id = typeof coord === 'object' && coord !== null && 'id' in coord ? (coord as any).id : coord;
                  if (!id) return false;
                  const idStr = String(id);
                  return idStr.trim() !== "";
                }).length === 0) && (
                  <p className="text-red-500 text-xs mt-2" role="alert">
                    At least one coordinator is required
                  </p>
                )}
                
                {formValidationAttempted && formData.coordinators && (formData.coordinators || []).filter((coord: any) => {
                  if (!coord) return false;
                  const id = typeof coord === 'object' && coord !== null && 'id' in coord ? (coord as any).id : coord;
                  if (!id) return false;
                  const idStr = String(id);
                  return idStr.trim() !== "";
                }).length > 3 && (
                  <p className="text-red-500 text-xs mt-2" role="alert">
                    Maximum of 3 coordinators allowed
                  </p>
                )}
              </div>
            </div>
            )}

            {/* Action Buttons */}
            <div className="bg-gray-50 rounded-xl p-6 mt-16">
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <Button
                variant="outline"
                onClick={() => window.location.href = "/dashboard"}
                className="px-6 py-3 h-12 border-gray-200 rounded-lg"
              >
                <ArrowLeft className="mr-2 h-4 w-4" /> Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={isCreatingJob}
                className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 h-14 font-medium rounded-lg"
              >
                {isCreatingJob ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Publishing...
                  </>
                ) : (
                  <>
                    Publish Job <ArrowRight className="ml-2 h-5 w-5" />
                  </>
                )}
              </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CreateJobPage
